import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { LayoutChangeEvent, StyleSheet, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Screen, FeatureTooltip } from '@components'
import { useLanguage } from '@context'
import { useGetGraphData, GraphNode, useScreen } from '@hooks'
import { ForceGraphCanvas } from './components/ForceGraph'
import { GraphControls } from './components/GraphControls'
import { GraphLegend } from './components/GraphLegend'

export const GraphScreen: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isDesktop } = useScreen()
  const styles = getStyles(theme)
  const navigation = useNavigation()

  /** Mobile/tablet: expanded panel overlays the graph (absolute). Desktop: expanded panel in the row. */
  const isOverlaySidebar = !isDesktop
  const [mode, setMode] = useState<'contacts-only' | 'full'>('contacts-only')
  const [edgeTypes, setEdgeTypes] = useState<string[]>(['contact'])
  const [searchText, setSearchText] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(isOverlaySidebar)
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })

  const onCanvasAreaLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout
    if (width > 0 && height > 0) {
      setDimensions({ width, height })
    }
  }, [])

  useEffect(() => {
    if (isOverlaySidebar) setIsCollapsed(true)
  }, [isOverlaySidebar])

  const { data: graphData, isLoading } = useGetGraphData({
    mode,
    edgeTypes: edgeTypes.join(','),
  })

  // Filter nodes by search
  const filteredData = useMemo(() => {
    if (!graphData) return { nodes: [], edges: [] }
    if (!searchText.trim()) return graphData

    const lowerSearch = searchText.toLowerCase()
    const matchingIds = new Set(
      graphData.nodes
        .filter((n) => n.label.toLowerCase().includes(lowerSearch))
        .map((n) => n.id),
    )

    return {
      nodes: graphData.nodes,
      edges: graphData.edges,
    }
  }, [graphData, searchText])

  const highlightedNodeId = useMemo(() => {
    if (!searchText.trim() || !graphData) return null
    const lowerSearch = searchText.toLowerCase()
    const match = graphData.nodes.find((n) => n.label.toLowerCase().includes(lowerSearch))
    return match?.id || null
  }, [graphData, searchText])

  const handleNodeClick = useCallback((node: GraphNode) => {
    if (node.type === 'contact' || node.type === 'ghost') {
      (navigation as any).navigate('Profile', {
        screen: 'DetailsGhostProfile',
        params: { profileId: node.id },
      })
    }
  }, [navigation])

  return (
    <Screen noPaddingBottom noPaddingTop>
      <View style={styles.wrapper}>
        <View style={styles.graphContainer}>
          {isCollapsed ? (
            <View style={styles.graphHeaderRow}>
              <View style={styles.graphTooltipSlot}>
                <FeatureTooltip
                  id="graph_intro"
                  title={t('featureTooltip.graph_intro.title')}
                  description={t('featureTooltip.graph_intro.description')}
                  icon="people"
                />
              </View>
              <View style={styles.sidebarCollapsedCompact}>
                <View style={styles.closeButtonCollapsed}>
                  <Icon
                    name="arrowLeft"
                    size={16}
                    color={theme.colors.lightGrey}
                    onPress={() => setIsCollapsed(false)}
                  />
                </View>
              </View>
            </View>
          ) : (
            <FeatureTooltip
              id="graph_intro"
              title={t('featureTooltip.graph_intro.title')}
              description={t('featureTooltip.graph_intro.description')}
              icon="people"
            />
          )}

          <View style={styles.graphCanvasArea} onLayout={onCanvasAreaLayout}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <Icon name="people" size={40} color={theme.colors.greyBlue} />
              </View>
            ) : (
              <ForceGraphCanvas
                graphData={filteredData}
                highlightedNodeId={highlightedNodeId}
                onNodeClick={handleNodeClick}
                width={dimensions.width}
                height={dimensions.height}
              />
            )}
          </View>

          <View style={styles.legendContainer}>
            <GraphLegend />
          </View>
        </View>

        {!isCollapsed && (
          <View
            style={[
              styles.sidebar,
              isOverlaySidebar && styles.sidebarOverlay,
              { width: 280 },
            ]}
          >
            <View style={styles.closeButton}>
              <Icon
                name="arrowRight"
                size={16}
                color={theme.colors.lightGrey}
                onPress={() => setIsCollapsed(true)}
              />
            </View>
            <GraphControls
              mode={mode}
              onModeChange={setMode}
              edgeTypes={edgeTypes}
              onEdgeTypesChange={setEdgeTypes}
              searchText={searchText}
              onSearchChange={setSearchText}
              nodeCount={filteredData.nodes.length}
              edgeCount={filteredData.edges.length}
            />
          </View>
        )}
      </View>
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      flexDirection: 'row',
      position: 'relative',
      minHeight: 0,
    },
    graphContainer: {
      flex: 1,
      alignSelf: 'stretch',
      minWidth: 0,
      minHeight: 0,
      backgroundColor: '#FAFBFC',
      position: 'relative',
      flexDirection: 'column',
    },
    /** Collapsed: tooltip + expand pill in normal flow (relative), not absolute over the graph. */
    graphHeaderRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      zIndex: 101,
    },
    graphTooltipSlot: {
      flex: 1,
      minWidth: 0,
    },
    graphCanvasArea: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      position: 'relative',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    legendContainer: {
      position: 'absolute',
      bottom: 12,
      left: 12,
    },
    sidebar: {
      backgroundColor: 'white',
      borderLeftWidth: 0.5,
      borderLeftColor: '#E5E5EA',
    },
    sidebarOverlay: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 100,
      maxWidth: '90%',
      shadowColor: '#000',
      shadowOffset: { width: -2, height: 0 },
      shadowOpacity: 0.12,
      shadowRadius: 10,
      elevation: 12,
    },
    sidebarCollapsedCompact: {
      width: 44,
      flexShrink: 0,
      backgroundColor: 'white',
      borderLeftWidth: 0.5,
      borderLeftColor: '#E5E5EA',
      borderTopLeftRadius: 14,
      borderBottomLeftRadius: 14,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: -2, height: 0 },
      shadowOpacity: 0.12,
      shadowRadius: 10,
      elevation: 12,
    },
    closeButton: {
      padding: 8,
      alignItems: 'flex-start',
      borderBottomWidth: 0.5,
      borderBottomColor: '#E5E5EA',
    },
    closeButtonCollapsed: {
      padding: 8,
      alignItems: 'center',
    },
  })
